import { useState } from 'react';

/**
 * Simple combobox: type-ahead select with "create new" affordance.
 * - results: array of { id, label }
 * - onSelect: callback when an existing item is picked
 * - onCreate: callback when user wants to create a brand-new item with the typed text
 */
export interface ComboboxItem { id: string; label: string; sublabel?: string }
export function Combobox({
  placeholder, value, onChange, results, onSelect, onCreate, createLabel = 'Add',
  loading, emptyLabel, autoOpen = false, mobileInlineResults = false,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  results: ComboboxItem[];
  onSelect: (item: ComboboxItem) => void;
  onCreate?: () => void;
  createLabel?: string;
  loading?: boolean;
  emptyLabel?: string;
  autoOpen?: boolean;
  mobileInlineResults?: boolean;
}) {
  const [open, setOpen] = useState(autoOpen);
  const showCreate = Boolean(onCreate) && value.trim().length >= 2 &&
    !results.some((r) => r.label.toLowerCase() === value.trim().toLowerCase());
  const hasContent = results.length > 0 || showCreate || loading;
  const showDropdown = open && (hasContent || (emptyLabel && !loading && results.length === 0));
  return (
    <div className="relative">
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={(e) => {
          setOpen(true);
          if (mobileInlineResults) {
            setTimeout(() => e.currentTarget.scrollIntoView({ block: 'start', behavior: 'smooth' }), 80);
          }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="w-full border rounded-lg px-3 py-3 text-base sm:py-2 sm:text-sm"
      />
      {showDropdown && (
        <ul
          className={[
            'z-10 mt-1 w-full overflow-auto bg-white border rounded-lg shadow-md text-sm overscroll-contain',
            mobileInlineResults
              ? 'relative max-h-[min(44vh,18rem)] sm:absolute sm:max-h-60'
              : 'absolute max-h-60',
          ].join(' ')}
        >
          {loading && results.length === 0 && (
            <li className="px-3 py-2 text-slate-400">Loading…</li>
          )}
          {!loading && results.length === 0 && !showCreate && emptyLabel && (
            <li className="px-3 py-2 text-slate-400">{emptyLabel}</li>
          )}
          {results.map((r) => (
            <li key={r.id}
                onMouseDown={() => onSelect(r)}
                className="px-3 py-3 sm:py-2 hover:bg-slate-50 cursor-pointer">
              <div className="font-medium text-slate-800">{r.label}</div>
              {r.sublabel && <div className="text-xs text-slate-500">{r.sublabel}</div>}
            </li>
          ))}
          {showCreate && (
            <li onMouseDown={() => onCreate?.()}
                className="px-3 py-3 sm:py-2 hover:bg-blue-50 cursor-pointer text-squishy-primary border-t">
              {createLabel} <strong>"{value.trim()}"</strong>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
